import {
  type DateStyle,
  type MeasurementKind,
  type TimeStyle,
  UNIT_CODES,
  type UnitCodeFor,
} from './types';

/**
 * What `csp-commonlib-internationalization` calls the things this package already models.
 *
 * profile-api ships two preference vocabularies at once: the original one it invented itself, and
 * the library's, which supersedes it. The two agree on meaning and disagree on almost every
 * string — `MILES` against `MILE`, `KWH` against `KILOWATT_HOUR`, `capacity` against `VOLUME`.
 * This module is the whole of that translation, kept in one file so a reader can check it against
 * the enums rather than hunting for the pairs.
 *
 * Written as *our code to their name* rather than the reverse, and typed as a total record, so a
 * unit added to {@link UNIT_CODES} fails to compile here until someone names its counterpart. The
 * lookups callers actually use are inverted from these tables at load, which is why no pair can be
 * declared in one direction and forgotten in the other.
 *
 * The unit *system* needs no table at all: this package adopted the library's three names
 * verbatim, so `METRIC`, `US_CUSTOMARY` and `IMPERIAL` already mean the same thing on both sides.
 * That was the point of taking the rename rather than mapping two names onto three.
 */

/**
 * Our measurement kind to the library's `Dimension`.
 *
 * Only the ten dimensions this package renders. The library also has `RATIO` and `DURATION`, which
 * have no wire kind here; a reader that resolves to one of those sends a key this table cannot
 * answer, and the resolver treats that as no opinion.
 */
export const DIMENSION_BY_KIND: Readonly<Record<MeasurementKind, string>> = {
  distance: 'DISTANCE',
  speed: 'SPEED',
  capacity: 'VOLUME',
  fuelConsumption: 'FUEL_CONSUMPTION',
  electricConsumption: 'ELECTRIC_CONSUMPTION',
  energy: 'ENERGY',
  pressure: 'PRESSURE',
  temperature: 'TEMPERATURE',
  emission: 'EMISSION',
  mass: 'MASS',
};

/**
 * Our unit code to the library's `MeasurementUnit`, per kind.
 *
 * Twenty-three pairs, each checked against both enum sources rather than inferred from the names.
 * `PSI` and `LITER` are the only two codes that survive the crossing unchanged.
 *
 * The library holds sixty-six units against these twenty-three. The rest are deliberately absent:
 * mapping them would spend a zero-dependency bundle on units no screen renders, and a reader only
 * reaches one through an explicit per-dimension override, which no preferences screen can yet
 * produce. An unmapped unit is reported and degrades to the locale answer.
 */
export const LIBRARY_UNIT_BY_CODE: {
  readonly [K in MeasurementKind]: Readonly<Record<UnitCodeFor<K>, string>>;
} = {
  distance: { KM: 'KILOMETER', MILES: 'MILE' },
  speed: {
    KMH: 'KILOMETER_PER_HOUR',
    MPH: 'MILE_PER_HOUR',
    MPS: 'METER_PER_SECOND',
  },
  capacity: {
    LITER: 'LITER',
    GAL_US: 'GALLON_US',
    GAL_UK: 'GALLON_IMPERIAL',
  },
  fuelConsumption: {
    L_PER_100KM: 'LITER_PER_100KM',
    MPG_US: 'MILE_PER_GALLON_US',
    MPG_UK: 'MILE_PER_GALLON_IMPERIAL',
  },
  electricConsumption: {
    KWH_PER_100KM: 'KILOWATT_HOUR_PER_100KM',
    MI_PER_KWH: 'MILE_PER_KILOWATT_HOUR',
  },
  energy: { KWH: 'KILOWATT_HOUR' },
  pressure: { KPA: 'KILOPASCAL', PSI: 'PSI' },
  temperature: { C: 'CELSIUS', F: 'FAHRENHEIT' },
  emission: {
    GR_PER_KM: 'GRAM_PER_KILOMETER',
    GR_PER_MI: 'GRAM_PER_MILE',
    LB_PER_MI: 'POUND_PER_MILE',
  },
  mass: { KG: 'KILOGRAM', LB: 'POUND' },
};

/** The library's `DateStyle` to ours. */
export const DATE_STYLE_BY_LIBRARY: Readonly<Record<string, DateStyle>> = {
  MONTH_DAY_YEAR: 'MDY',
  DAY_MONTH_YEAR: 'DMY',
  YEAR_MONTH_DAY: 'YMD',
};

/** The library's `ClockConvention` to our time style. */
export const CLOCK_BY_LIBRARY: Readonly<Record<string, TimeStyle>> = {
  TWELVE_HOUR: '12H',
  TWENTY_FOUR_HOUR: '24H',
};

const invert = <K extends MeasurementKind>(
  kind: K
): ReadonlyMap<string, UnitCodeFor<K>> => {
  const pairs = Object.entries(LIBRARY_UNIT_BY_CODE[kind]) as [
    UnitCodeFor<K>,
    string,
  ][];
  return new Map(pairs.map(([code, libraryUnit]) => [libraryUnit, code]));
};

const CODE_BY_LIBRARY_UNIT = Object.fromEntries(
  (Object.keys(UNIT_CODES) as MeasurementKind[]).map((kind) => [
    kind,
    invert(kind),
  ])
) as { readonly [K in MeasurementKind]: ReadonlyMap<string, UnitCodeFor<K>> };

const KIND_BY_DIMENSION: ReadonlyMap<string, MeasurementKind> = new Map(
  (Object.keys(DIMENSION_BY_KIND) as MeasurementKind[]).map((kind) => [
    DIMENSION_BY_KIND[kind],
    kind,
  ])
);

/**
 * The measurement kind a library `Dimension` names, or `undefined` for one this package has no
 * wire field for — `RATIO` and `DURATION`.
 *
 * @param dimension - A library `Dimension` constant name, as it arrives on the wire.
 * @returns The matching kind, or `undefined` when the dimension is not modelled here.
 */
export const kindOfDimension = (
  dimension: string
): MeasurementKind | undefined => KIND_BY_DIMENSION.get(dimension);

/**
 * This package's code for a library unit name.
 *
 * @param kind - The measurement kind the unit belongs to.
 * @param libraryUnit - A library `MeasurementUnit` constant name.
 * @returns Our equivalent code, or `undefined` when the unit is one we do not render.
 */
export const unitCodeOfLibraryUnit = <K extends MeasurementKind>(
  kind: K,
  libraryUnit: string
): UnitCodeFor<K> | undefined => CODE_BY_LIBRARY_UNIT[kind].get(libraryUnit);

/**
 * The library unit name for one of this package's codes, for a request that names its units in
 * the library's vocabulary. The inverse of {@link unitCodeOfLibraryUnit}.
 *
 * @param kind - The measurement kind the code belongs to.
 * @param code - This package's unit code.
 * @returns The library `MeasurementUnit` constant name.
 */
export const libraryUnitOf = <K extends MeasurementKind>(
  kind: K,
  code: UnitCodeFor<K>
): string => LIBRARY_UNIT_BY_CODE[kind][code];
